/**
 * `@zep/logger/tracking` — 분석 이벤트(track) 타입 강제 프리미티브 (React 무의존).
 *
 * - shape: 자주 쓰는 payload 모양 (토글/전이/저장/노출/클릭)
 * - builder: 호출부가 모양을 벗어날 수 없게 강제 (수기 객체 금지)
 * - trackEvent / createEventTracker: 타입드 명령형 트래킹 진입점
 *
 * 도메인은 `declare module '@zep/logger'` 로 `EventMap` 을 증강해 이름/타입을 강제한다.
 * (React의 노출·클릭 컴포넌트는 `@zep/logger/react`)
 */
import type { EventMap, OtelSafeValue, TrackOptions } from '../logger/types';
import { logger } from '../runtime';

export { onPageLeave, onPageReturn } from './pageLeave';

// ─── shapes ──────────────────────────────────────────────────────────────────

/** 불리언 토글 on/off. 켜짐=true. */
export type ToggleEvent = { value: boolean };

/** 값 A→B 전이. `before_<thing>` / `after_<thing>` 평탄화. */
export type Transition<T extends string = string> = {
  [K in `before_${T}` | `after_${T}`]: OtelSafeValue;
};

/** 폼 저장/제출 결과. 변경 필드(before_/after_)를 같이 실을 수 있음. */
export interface SaveEvent {
  result: 'success' | 'fail';
  reason?: string;
  [key: string]: OtelSafeValue;
}

/** 노출/조회 (보일 때 1회). */
export type ViewEvent = { section?: string };

/** 정적 클릭 (고유 payload 없음). */
export type ClickEvent = Record<string, never>;

// ─── builders ────────────────────────────────────────────────────────────────

/** 토글 변경 후 값 → `{ value }`. (`e.currentTarget.checked` 를 넘긴다) */
export function buildToggleChange(checked: boolean): ToggleEvent {
  return { value: checked };
}

/**
 * 단일 값 전이를 `before_<thing>` / `after_<thing>` 로 만든다.
 *   buildTransition('range', 'all', 'spotlight') -> { before_range: 'all', after_range: 'spotlight' }
 */
export function buildTransition<Thing extends string, T extends OtelSafeValue>(
  thing: Thing,
  before: T,
  after: T,
): Record<`before_${Thing}` | `after_${Thing}`, T> {
  return {
    [`before_${thing}`]: before,
    [`after_${thing}`]: after,
  } as Record<`before_${Thing}` | `after_${Thing}`, T>;
}

// ─── 명령형 진입점 ─────────────────────────────────────────────────────────────

/**
 * React·비React 어디서나 쓰는 타입드 트래킹.
 * 이름/prop 은 `EventMap` 으로 컴파일 강제. 발화는 ZEPLogger 그대로.
 */
export function trackEvent<K extends keyof EventMap & string>(
  name: K,
  props: EventMap[K],
  options?: TrackOptions,
): void {
  if (options) logger.track(name, props, options);
  else logger.track(name, props);
}

/**
 * 공통 context(예: 화면/세션 메타)를 자동 주입하는 트래커를 만든다.
 * context 는 호출 시점에 평가되도록 getter 로 받는다.
 *
 *   const track = createEventTracker(() => ({ place: 'game' }));
 *   track('some_event', { value: true });
 */
export function createEventTracker(
  getContext: () => Record<string, OtelSafeValue>,
) {
  return <K extends keyof EventMap & string>(
    name: K,
    props: EventMap[K],
  ): void => {
    logger.track(name as string, {
      ...getContext(),
      ...(props as Record<string, unknown>),
    });
  };
}
